import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import { ReactComponent as CloseIcon } from '@rocco/icons/svg/close.svg';
import type { popoverContentVariants } from '@rocco/components/popover';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import type { VariantProps } from 'class-variance-authority';
import { Button } from '@rocco/components/button';
import { Badge } from '@rocco/components/badge';
import { useBoolean } from 'ahooks';
import { cn } from '@rocco/utils/cn';

interface FilterDropdownProps extends React.PropsWithChildren {
    title: string;
    selected?: boolean;
    /** Optional badge count to show above the trigger. */
    badgeCount?: number;
    closeOnScroll?: boolean;
    popoverVariant?: VariantProps<typeof popoverContentVariants>['variant'];
    popoverBackground?: VariantProps<
        typeof popoverContentVariants
    >['background'];
    /** Callback to clear all filters in this section. When provided and selected=true, shows X button. */
    onClear?: () => void;
    /** Optional className to apply to the trigger button (e.g. to override pill colors). */
    triggerClassName?: string;
    /** Optional className to apply to the popover content (e.g. for custom frosted glass styling). */
    popoverClassName?: string;
}

interface FilterDropdownContextType {
    isOpen: boolean;
    closeFilterDropdown: () => void;
    /** Ref for child components to register a callback that fires when the popover closes (before content unmounts). */
    onCloseCallbackRef: React.MutableRefObject<(() => void) | null>;
}

const FilterDropdownContext = createContext<
    FilterDropdownContextType | undefined
>(undefined);

export const useFilterDropdownContext = () => {
    const context = useContext(FilterDropdownContext);

    if (!context) {
        throw new Error(
            'useFilterDropdownContext must be used within a FilterDropdownProvider',
        );
    }

    return context;
};

export const FilterDropdown = (props: FilterDropdownProps) => {
    const {
        children,
        title,
        badgeCount,
        closeOnScroll = false,
        popoverVariant,
        popoverBackground,
        popoverClassName,
        onClear,
        triggerClassName,
    } = props;
    const [isOpen, { set: setIsOpen, setFalse: closeFilterDropdown }] =
        useBoolean(false);
    const onCloseCallbackRef = useRef<(() => void) | null>(null);

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            try {
                onCloseCallbackRef.current?.();
            } finally {
                setIsOpen(open);
            }
        } else {
            setIsOpen(open);
        }
    };

    useEffect(() => {
        let lastScrollY = window.scrollY;

        const handleScroll = () => {
            if (!closeOnScroll) return;
            const scrollDiff = Math.abs(window.scrollY - lastScrollY);
            if (scrollDiff > 300) {
                try {
                    onCloseCallbackRef.current?.();
                } finally {
                    closeFilterDropdown();
                }
                lastScrollY = window.scrollY;
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [closeFilterDropdown, closeOnScroll]);

    /** Memoize context value to prevent unnecessary re-renders of consuming components */
    const contextValue = useMemo(
        () => ({ isOpen, closeFilterDropdown, onCloseCallbackRef }),
        [isOpen, closeFilterDropdown],
    );

    const handleClearClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Don't open dropdown when clicking X
        e.preventDefault();
        onClear?.();
    };

    const showClearButton = props.selected && onClear;
    const hasBadge = (badgeCount ?? 0) > 0;

    return (
        <FilterDropdownContext.Provider value={contextValue}>
            <Popover open={isOpen} onOpenChange={handleOpenChange}>
                {/* Wrapper div for badge positioning outside the button */}
                <div className="relative">
                    <PopoverTrigger asChild>
                        <Button
                            color="secondary"
                            layoutMode="noShrink"
                            weight="normal"
                            className={cn(
                                triggerClassName,
                                props.selected &&
                                    'border-[#161616] bg-[#161616] fill-white text-white hover:border-[#2a2a2a] hover:bg-[#2a2a2a]',
                            )}
                        >
                            <span
                                className={cn(props.selected && 'font-medium')}
                            >
                                {title}
                            </span>
                            {showClearButton ? (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Clear ${title} filters`}
                                    onClick={handleClearClick}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            e.preventDefault();
                                            handleClearClick(
                                                e as unknown as React.MouseEvent,
                                            );
                                        }
                                    }}
                                    className="ml-2 flex size-4.5 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 transition-colors hover:bg-white/20"
                                >
                                    <CloseIcon className="size-4" />
                                </span>
                            ) : (
                                <ArrowChevronDownIcon className="group-data-[state=open]:transform-[rotateX(180deg)] ml-2 size-4.5 transition-transform" />
                            )}
                        </Button>
                    </PopoverTrigger>
                    {/* Badge positioned at top-right of button, top edge aligned with button top */}
                    {hasBadge && (
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute -right-1 -top-0.5 z-20"
                        >
                            <Badge size="square" text="default">
                                {badgeCount! > 9 ? '9+' : badgeCount}
                            </Badge>
                        </span>
                    )}
                </div>
                <PopoverContent
                    align="start"
                    paddings="small"
                    avoidCollisions={false}
                    variant={popoverVariant}
                    background={popoverBackground}
                    className={popoverClassName}
                >
                    {children}
                </PopoverContent>
            </Popover>
        </FilterDropdownContext.Provider>
    );
};
